import {assert} from 'Asserts';
import {
  ReviewerStatus,
  ReviewLifecycleType,
  ReviewStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {ReviewLifecycleActionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {capitalize} from 'util/NameUtil';
import sortedBy from 'util/sortedBy';

import type {ReviewLifecycleSummaryPanelQuery} from './__generated/ReviewLifecycleSummaryPanelQuery';
import {
  REVIEW_ACTION_TO_DISPLAY_PAST_TENSE,
  REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT,
} from './review-lifecycles/ReviewLifecycleConstants';
import type {ReviewLifecycleSummaryAvatar} from './review-lifecycles/ReviewLifecycleSummaryItem';
import {teamHasValidNonAuthorReviewersForReviewable} from './review-lifecycles/ReviewLifecyclesUtil';
import {ReviewAction, ReviewBadgeStatus, Snapshot} from './ReviewConstants';
import {canSendForReviewFromStatus} from './ReviewUtil';

type ReviewLifecycleSummaryOrInaccessibleGraphQL = ReviewLifecycleSummaryPanelQuery['reviewable'];

type ReviewableGraphQL = ExcludeInaccessible<ReviewLifecycleSummaryOrInaccessibleGraphQL>;

type ReviewGraphQL = NonNullable<ReviewableGraphQL['review']>;

type ActiveReviewLifecycleVersionGraphQL = NonNullable<ReviewGraphQL['activeReviewLifecycleVersion']>;

export type ReviewLifecycleGraphQL = NonNullable<ActiveReviewLifecycleVersionGraphQL['reviewLifecycle']>;

export type ReviewChangeGraphQL = ReviewGraphQL['reviewChanges'][number];

export type ScopedReviewLifecycleVersionGraphQL = NonNullable<ReviewChangeGraphQL['reviewLifecycleVersion']>;

export type CreatorGraphQL = ReviewChangeGraphQL['creator'];

export type ReviewerGraphQL = NonNullable<ReviewGraphQL['reviewers']>[number];

/** Minimal review-change fields used to scope cyclical review history to a version. */
export interface VersionScopedReviewChange {
  newStatus: ReviewStatus | null;
  reviewItemVersionApiIdentifier?: string | null;
}

/** Panel and badge derivation both scope review changes to the open version (or not, for linear). */
export interface VersionScopedPanelContext<
  TChange extends VersionScopedReviewChange = VersionScopedReviewChange,
> {
  scopedReviewChanges: ReadonlyArray<TChange>;
  scopedStatus: ReviewStatus | null;
  isActiveCycleVersion: boolean;
  panelStatus: ReviewStatus;
}

export interface ReviewBadgeGatingResult {
  showSendForReview: boolean;
  showSummaryPanel: boolean;
}

export interface ReviewBadgeGatingInput {
  enableReviewLifecycle: boolean | undefined;
  canSendForReview: boolean | null;
  parentReviewLifecycleCount: number;
  reviewUsesReviewLifecycle: boolean;
}

export interface HiddenReviewBadgeDerivation {
  isHidden: true;
  badgeStatus: ReviewBadgeStatus.IN_PROGRESS;
  reviewStatus: undefined;
  showSendForReview: false;
  showSummaryPanel: false;
}

export interface VisibleReviewBadgeDerivation {
  isHidden: false;
  badgeStatus: ReviewBadgeStatus;
  reviewStatus: ReviewStatus | undefined;
  showSendForReview: boolean;
  showSummaryPanel: boolean;
}

export type ReviewBadgeDerivation = HiddenReviewBadgeDerivation | VisibleReviewBadgeDerivation;

export interface AuthorAction {
  primaryReviewAction: ReviewAction.RETRACT | ReviewAction.SEND_FOR_REVIEW;
  userFacingMessage: string;
}

export interface ReviewStageViewModel {
  avatar: ReviewLifecycleSummaryAvatar;
  reviewStageAction: string;
  canReassign: boolean;
  canReview: boolean;
  hasValidReviewerReplacements: boolean;
  wasEsigned: boolean;
  message: string | null | undefined;
}

/**
 * Last change in time order with a non-null newStatus (comments are skipped).
 *
 * @param reviewChanges Time-ordered array of all review changes on the review (ascending by createdAt).
 */
export function getLastStatusBearingChange(
  reviewChanges: ReadonlyArray<VersionScopedReviewChange>
): VersionScopedReviewChange | null {
  return reviewChanges.findLast((change) => change.newStatus != null) ?? null;
}

/**
 * Scoped status for the open version: last status-bearing change matching openVersionId, else null.
 *
 * @param reviewChanges Time-ordered array of all review changes on the review (ascending by createdAt).
 */
export function getCycleScopedStatus(
  reviewChanges: ReadonlyArray<VersionScopedReviewChange>,
  openVersionId: string
): ReviewStatus | null {
  return (
    reviewChanges.findLast(
      (change) => change.reviewItemVersionApiIdentifier === openVersionId && change.newStatus != null
    )?.newStatus ?? null
  );
}

/**
 * Version id of the most recent status-bearing change across the entire review (the live cycle).
 *
 * @param reviewChanges Time-ordered array of all review changes on the review (ascending by createdAt).
 */
export function getActiveCycleVersionId(
  reviewChanges: ReadonlyArray<VersionScopedReviewChange>
): string | null {
  return getLastStatusBearingChange(reviewChanges)?.reviewItemVersionApiIdentifier ?? null;
}

/**
 * Maps a review status to the badge status used to render the review badge's color/icon variant.
 */
export function getBadgeStatus(status?: ReviewStatus): ReviewBadgeStatus {
  switch (status) {
    case ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.ACCEPTED:
    case ReviewStatus.CYCLE_COMPLETED:
      return ReviewBadgeStatus.ACCEPTED;
    case ReviewStatus.NEEDS_REVIEW:
    case ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS:
      return ReviewBadgeStatus.IN_REVIEW;
    case ReviewStatus.REJECTED:
      return ReviewBadgeStatus.REJECTED;
    case ReviewStatus.ACTION_REQUIRED:
      return ReviewBadgeStatus.ACTION_REQUIRED;
    case ReviewStatus.RETRACTED:
    case null:
    default:
      return ReviewBadgeStatus.IN_PROGRESS;
  }
}

/** Whether the open version's scoped review status allows publishing. */
export function isPublishApprovedForScopedStatus(scopedStatus: ReviewStatus | null): boolean {
  return getBadgeStatus(scopedStatus ?? undefined) === ReviewBadgeStatus.ACCEPTED;
}

function shouldShowSendForReview({
  enableReviewLifecycle,
  canSendForReview,
  parentReviewLifecycleCount,
  reviewStatus,
  reviewUsesReviewLifecycle,
}: {
  enableReviewLifecycle: boolean | undefined;
  canSendForReview: boolean | null;
  parentReviewLifecycleCount: number;
  reviewStatus: ReviewStatus | null | undefined;
  reviewUsesReviewLifecycle: boolean;
}): boolean {
  if (
    !enableReviewLifecycle ||
    !canSendForReview ||
    parentReviewLifecycleCount === 0 ||
    !canSendForReviewFromStatus(reviewStatus ?? undefined)
  ) {
    return false;
  }

  if (reviewStatus === ReviewStatus.REJECTED) {
    // Only show send for review for rejected reviewables that were rejected using legacy review process.
    // If it was rejected using a review lifecycle, we should show the summary panel.
    return !reviewUsesReviewLifecycle;
  }
  return true;
}

/**
 * Decides whether the review badge should offer a send-for-review dropdown, drop down into the review
 * lifecycle summary panel, or render as a plain status badge.
 *
 * For accepted, rejected, and in review reviewables, if the associated review is linked to a review
 * lifecycle, the summary panel is shown. For in progress reviewables, if the project has a review
 * lifecycle and the user has send-for-review permissions, the send-for-review dropdown is shown.
 */
export function getBadgeGating({
  enableReviewLifecycle,
  canSendForReview,
  parentReviewLifecycleCount,
  reviewStatus,
  reviewUsesReviewLifecycle,
}: ReviewBadgeGatingInput & {
  reviewStatus: ReviewStatus | null | undefined;
}): ReviewBadgeGatingResult {
  const showSendForReview = shouldShowSendForReview({
    enableReviewLifecycle,
    canSendForReview,
    parentReviewLifecycleCount,
    reviewStatus,
    reviewUsesReviewLifecycle,
  });

  const showSummaryPanel =
    !showSendForReview &&
    reviewUsesReviewLifecycle &&
    getBadgeStatus(reviewStatus ?? undefined) !== ReviewBadgeStatus.IN_PROGRESS;

  return {showSendForReview, showSummaryPanel};
}

/** Whether the scoped review-change slice includes a send-for-review action. */
export function reviewChangesIncludeSendForReview(
  reviewChanges: ReadonlyArray<ReviewChangeGraphQL>
): boolean {
  return reviewChanges.some((change) => change.action === ReviewAction.SEND_FOR_REVIEW);
}

/**
 * Whether sending for review should be blocked because a project self-review lifecycle is incompatible
 * with the reviewable still carrying review criteria. Used to surface a blocking error on the badge.
 */
export function hasSelfReviewCriteriaConflict({
  hasReviewCriteria,
  projectLifecycleTypes,
}: {
  hasReviewCriteria: boolean;
  projectLifecycleTypes: Array<ReviewLifecycleType>;
}): boolean {
  return hasReviewCriteria && projectLifecycleTypes.includes(ReviewLifecycleType.SELF_REVIEW);
}

/**
 * Determines the author's primary review action (retract vs. resend) and its user-facing label for a
 * given review status, or null if no author action applies.
 */
export function getAuthorAction(
  status: ReviewStatus,
  activeReviewLifecycle: ReviewLifecycleGraphQL | null
): AuthorAction | null {
  switch (status) {
    case ReviewStatus.ACCEPTED:
      return {primaryReviewAction: ReviewAction.RETRACT, userFacingMessage: 'Reopen for review'};
    case ReviewStatus.CYCLE_COMPLETED:
      return null;
    case ReviewStatus.RETRACTED:
    case ReviewStatus.REJECTED:
      // activeReviewLifecycle is the active lifecycle version's parent lifecycle, which is nullable
      // (e.g. the lifecycle was deleted or is inaccessible). Without a lifecycle to resend into, there
      // is no author action to offer for retracted/rejected reviewables.
      if (activeReviewLifecycle != null) {
        return {primaryReviewAction: ReviewAction.SEND_FOR_REVIEW, userFacingMessage: 'Resend for review'};
      }
      return null;
    default:
      return {primaryReviewAction: ReviewAction.RETRACT, userFacingMessage: 'Cancel review'};
  }
}

/**
 * Find a review change corresponding to a given review stage since the last review.
 *
 * @param reviewStageId ID of the review stage which was completed
 * @param reviewChanges Time-ordered array of all review changes on a reviewable object.
 */
export function findLastReviewChangeForStage(
  reviewStageId: string,
  reviewChanges: Array<ReviewChangeGraphQL>
): ReviewChangeGraphQL | null {
  for (const reviewChange of [...reviewChanges].reverse()) {
    if (reviewChange.action === ReviewAction.SEND_FOR_REVIEW) {
      return null;
    }
    if (reviewChange.previousStage?.id === reviewStageId) {
      return reviewChange;
    }
  }
  return null;
}

/**
 * Finds the most recent review change where the reviewable was sent for review by the user.
 *
 * When blocking snapshots are enabled, there are two SEND_FOR_REVIEW review changes.
 * The first is when the user actually sends reviewable for review, and the second is when the snapshot completes
 * This function ensures we grab that first review change in that case.
 * @param reviewChanges Time-ordered array of all review changes on a reviewable.
 */
export function findLastSentForReviewFromUserWithTimestamp(reviewChanges: Array<ReviewChangeGraphQL>): {
  author: CreatorGraphQL;
  timestamp: string;
  wasEsigned: boolean;
  message: string | null;
} {
  assert(reviewChanges.length > 0, 'Reviewable item has not had any review change');
  const reversedChanges = [...reviewChanges].reverse();

  let lastSendForReviewIndex = reversedChanges.findIndex(
    ({action}) => action === ReviewAction.SEND_FOR_REVIEW
  );
  assert(lastSendForReviewIndex !== -1, 'Reviewable item should have been sent for review');

  // Searches backwards to check for a blocking snapshot since that would be the user's actual sent for review timestamp.
  // It may not be the immediately previous review change as users can create comment review changes while a snapshot is in progress.
  for (let i = lastSendForReviewIndex; i < reversedChanges.length; i++) {
    const status = reversedChanges[i].newStatus;
    if (status === ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS) {
      lastSendForReviewIndex = i;
      break;
      // If we find RETRACT/REJECT before REVIEW_SNAPSHOT_IN_PROGRESS, review doesn't have a blocking snapshot and we exit early.
    } else if (status === ReviewStatus.RETRACTED || status === ReviewStatus.REJECTED) {
      break;
    }
  }
  const lastSentForReviewByUser = reversedChanges[lastSendForReviewIndex];
  return {
    author: lastSentForReviewByUser.creator,
    timestamp: lastSentForReviewByUser.createdAt,
    wasEsigned: lastSentForReviewByUser.wasEsigned || false,
    message: lastSentForReviewByUser.message,
  };
}

/**
 * Finds the most recent snapshot for the current review lifecycle.
 *
 * @param reviewChanges Time-ordered array of all review changes on a reviewable.
 * @returns The most recent snapshot for the current review lifecycle if found, else null.
 */
export function findSnapshotForCurrentReviewLifecycle(
  reviewChanges: ReadonlyArray<ReviewChangeGraphQL>
): Snapshot | null {
  if (reviewChanges.length === 0) {
    return null;
  }

  const reversedChanges = [...reviewChanges].reverse();

  for (const item of reversedChanges) {
    // When blocking snapshots are enabled, two review changes objects with the SEND_FOR_REVIEW action will be
    // created:
    // 1. User-initiated action when review is sent (with snapshot attached)
    // 2. Auto-created action when snapshot completes (no snapshot attached)
    // We want to skip the second auto-created one (which would be the most recent one if it exists) and
    // instead return the first user-initiated one
    if (item.wasCreatedBySnapshot) {
      continue;
    }

    if (item.action === ReviewAction.SEND_FOR_REVIEW) {
      return item.snapshot ?? null;
    }

    // If we encounter a RETRACTED or REJECTED status before finding the SEND_FOR_REVIEW action, it means we
    // do not currently have an active review, so return null
    if (item.newStatus === ReviewStatus.RETRACTED || item.newStatus === ReviewStatus.REJECTED) {
      return null;
    }
  }

  return null;
}

export interface HistoricalReviewStage {
  /** Stable key for the rendered row (the source review change id). */
  key: string;
  /** Id of the review lifecycle stage that was acted on, if known. */
  stageId: string | null;
  /** Name of the acted-on stage, if the change recorded one. */
  stageName: string | null;
  /** The user (or app/service account) who took the action, used as the row's avatar/name. */
  creator: CreatorGraphQL;
  reviewStatus: ReviewerStatus.FINISHED | ReviewerStatus.REJECTED;
  /** Stage action type (accept/approve/witness/...) used to label the completed row, if recorded. */
  reviewLifecycleAction: ReviewLifecycleActionType | undefined;
  completedTime: string;
  wasEsigned: boolean;
  message: string | null;
}

const HISTORICAL_SUB_CYCLE_BOUNDARY_ACTIONS: ReadonlySet<ReviewAction> = new Set([
  ReviewAction.SEND_FOR_REVIEW,
  ReviewAction.RETRACT,
]);

function getChangesFromLastHistoricalSubCycleBoundary(
  scopedReviewChanges: ReadonlyArray<ReviewChangeGraphQL>
): ReadonlyArray<ReviewChangeGraphQL> {
  const lastHistoricalSubCycleBoundaryIndex = scopedReviewChanges.findLastIndex(
    (change) => change.action != null && HISTORICAL_SUB_CYCLE_BOUNDARY_ACTIONS.has(change.action)
  );
  if (lastHistoricalSubCycleBoundaryIndex === -1) {
    return scopedReviewChanges;
  }
  return scopedReviewChanges.slice(lastHistoricalSubCycleBoundaryIndex);
}

/**
 * Builds read-only stage rows for a historical cycle from version-scoped review changes.
 * Live `review.reviewers` only reflects the active cycle, so prior versions must reconstruct
 * from stamped changes. Accept/reject become rows; input must be one version, chronological.
 *
 * Terminal historical cycles only include stages that received an accept/reject — un-acted
 * pending/blocked stages are omitted by design.
 */
export function getHistoricalReviewStages(
  scopedReviewChanges: ReadonlyArray<ReviewChangeGraphQL>
): Array<HistoricalReviewStage> {
  const stagesWithChanges: Array<{change: ReviewChangeGraphQL; stage: HistoricalReviewStage}> = [];
  getChangesFromLastHistoricalSubCycleBoundary(scopedReviewChanges).forEach((change) => {
    if (change.action !== ReviewAction.ACCEPT && change.action !== ReviewAction.REJECT) {
      return;
    }
    stagesWithChanges.push({
      change,
      stage: {
        key: change.id,
        stageId: change.previousStage?.id ?? null,
        stageName: change.previousStage?.name ?? null,
        creator: change.creator,
        reviewStatus:
          change.action === ReviewAction.REJECT ? ReviewerStatus.REJECTED : ReviewerStatus.FINISHED,
        reviewLifecycleAction: change.previousStage?.action ?? undefined,
        completedTime: change.createdAt,
        wasEsigned: change.wasEsigned ?? false,
        message: change.message,
      },
    });
  });
  return sortedBy(stagesWithChanges, [
    (row) => row.change.previousStage?.sequentialPositionIndex ?? Number.POSITIVE_INFINITY,
    (row) => row.stage.stageId ?? '',
    (row) => row.stage.completedTime,
  ]).map(({stage}) => stage);
}

/**
 * Returns the review lifecycle version that was stamped on the given (version-scoped) review changes.
 *
 * The review-level `activeReviewLifecycleVersion` only describes the currently-active cycle and is null
 * when the current version has not been sent for review, so viewing a previously reviewed version must
 * fall back to the version that its own changes recorded. Changes are appended chronologically, so the
 * last stamped version is the one that governed this cycle.
 */
export function getScopedReviewLifecycleVersion(
  scopedReviewChanges: ReadonlyArray<ReviewChangeGraphQL>
): ScopedReviewLifecycleVersionGraphQL | null {
  return (
    scopedReviewChanges.findLast((change) => change.reviewLifecycleVersion != null)?.reviewLifecycleVersion ??
    null
  );
}

/**
 * Resolves whether the current user can reassign a review stage and whether the stage still has valid
 * non-author reviewers to reassign to.
 *
 * An author with reassignment permission can always reassign. For team stages, a non-author can reassign
 * only when they belong to the stage's team and both the reviewer and the stage opt into team
 * reassignment.
 */
function getStageReassignability({
  canReassign,
  isCurrentUserAuthor,
  authorIds,
  currentUserId,
  team,
  isReviewerTeamReassignable,
  isStageTeamReassignable,
}: {
  canReassign: boolean;
  isCurrentUserAuthor: boolean;
  authorIds: Array<string>;
  currentUserId: string | null;
  team: ReviewerGraphQL['reviewStage']['selectedTeam'];
  isReviewerTeamReassignable: boolean | null;
  isStageTeamReassignable: boolean | null;
}): {canReassign: boolean; hasValidReviewers: boolean} {
  // canReassign is True when the reviewable's review status is NEEDS_REVIEW, the user has
  // READ permissions for this reviewable item, and the user is a reviewable author.
  if (isCurrentUserAuthor || !canReassign || team == null) {
    return {canReassign: canReassign && isCurrentUserAuthor, hasValidReviewers: true};
  }

  // checks team-based review stages when the User is not an author and has READ permissions for this
  // NEEDS_REVIEW reviewable for team-specific re-assignability
  const memberIds = team.members.map((member) => member.id);
  const hasValidReviewers = teamHasValidNonAuthorReviewersForReviewable(memberIds, authorIds);
  // if the user is a part of the stage's assigned team, allow the user to reassign stage.
  // this will be checked with the team member reassignment checkbox being checked later.
  const canReassignAsTeamReviewer = currentUserId != null && memberIds.includes(currentUserId);
  // if the team member reassignment checkbox is checked, any team member can reassign that stage
  const isReassignableByTeamMember = Boolean(
    canReassignAsTeamReviewer && isReviewerTeamReassignable && isStageTeamReassignable
  );
  return {canReassign: isReassignableByTeamMember, hasValidReviewers};
}

/**
 * Derives the per-stage state (avatar, action label, reassignment/review permissions, e-signature and
 * comment message) rendered for a single reviewer row in the review lifecycle summary panel.
 *
 * Returns null when the stage has no assigned reviewer (which the panel renders as a skipped stage).
 */
export function getReviewStageViewModel({
  reviewer,
  canReassign,
  lifecycleType,
  status,
  authors,
  reviewChanges,
  currentUserId,
}: {
  reviewer: ReviewerGraphQL;
  canReassign: boolean;
  lifecycleType: ReviewLifecycleType;
  status: ReviewStatus;
  authors: Array<{id: string}> | undefined;
  reviewChanges: Array<ReviewChangeGraphQL>;
  currentUserId: string | null;
}): ReviewStageViewModel | null {
  const {user, reviewStatus, reviewStage, isTeamReassignable} = reviewer;
  const {action, selectedTeam: team, isTeamReassignable: isTeamReassignableFromStage} = reviewStage;

  if (user == null) {
    // TODO(BNCH-42332): Handle required reassignment since reviewer does not exist for the stage
    return null;
  }

  const authorIds = authors?.map(({id}) => id) ?? [];
  const isCurrentUserAuthor = authorIds.includes(currentUserId ?? '');

  const {canReassign: canReassignRLCSummaryItem, hasValidReviewers} = getStageReassignability({
    canReassign,
    isCurrentUserAuthor,
    authorIds,
    currentUserId,
    team,
    isReviewerTeamReassignable: isTeamReassignable,
    isStageTeamReassignable: isTeamReassignableFromStage,
  });

  const avatar: ReviewLifecycleSummaryAvatar = {
    id: user.id,
    avatar_url: user.avatarUrl,
    email: user.__typename === 'UserNode' ? user.email : undefined,
    handle: user.handle,
    name: user.name,
  };
  const reviewStageAction =
    reviewStatus === ReviewerStatus.FINISHED || lifecycleType === ReviewLifecycleType.SELF_REVIEW
      ? REVIEW_ACTION_TO_DISPLAY_PAST_TENSE[action]
      : capitalize(REVIEW_ACTION_TYPE_TO_DISPLAY_TEXT[action]);

  const reviewChange = findLastReviewChangeForStage(reviewStage.id, reviewChanges);
  let wasEsigned = false;
  if (reviewStatus === ReviewerStatus.FINISHED || reviewStatus === ReviewerStatus.REJECTED) {
    wasEsigned = reviewChange?.wasEsigned || false;
  }

  return {
    avatar,
    reviewStageAction,
    canReassign: canReassignRLCSummaryItem,
    canReview: avatar.id === currentUserId && status !== ReviewStatus.REJECTED,
    hasValidReviewerReplacements: hasValidReviewers,
    wasEsigned,
    message: reviewChange?.message,
  };
}
